import type { ReactNode } from "react";

// A row of KPI cards joined by arithmetic operators, which wraps.
//
// The operator belongs to the card that FOLLOWS it, not to the gap between two
// cards, and that is the whole point of this component. As siblings of the
// cards the operators could not wrap with them: the row was a single
// non-wrapping flex line, so on a narrower window the cards shrank past their
// content and the last one ran off the right-hand edge.
//
// Paired with its card, an operator travels to the next line when its card
// does, and the equation still reads in order -- top-left to bottom-right, each
// continued line opening with the operator that carries it, the way an equation
// broken across a line is written on paper.
//
// Every card reserves the gutter, the first one included, so all of them are the
// same width. The first used to skip it -- one gutter wider than the rest, on the
// grounds that it is the left-hand side of the equation -- but the extra width
// read as an accident rather than as emphasis, and equal cards are what makes the
// row scannable.

export interface FormulaItem {
  key: string;
  /** Operator rendered before this card. Omitted on the first item. */
  op?: string;
  /**
   * Kept for callers that mark a card as not being a term of the equation. The
   * gutter is now reserved either way, so this is documentation rather than
   * layout.
   */
  spacer?: boolean;
  card: ReactNode;
}

interface FormulaCardRowProps {
  items: FormulaItem[];
  /**
   * Wrap threshold, gutter included. A card must not shrink below what its
   * delta rows need, and that figure is measured rather than guessed: bisecting
   * the column width until the delta rows overflow puts it at 220px for the
   * Utilization row and 226px for Capacity, whose values and units run a little
   * wider. The default is the larger of the two plus 4px, since text metrics
   * are not identical on every machine and the penalty for being 1px under is
   * an overlapping row rather than a wrapped one.
   */
  minItemWidth?: number;
}

// Narrow on purpose. Every pixel here comes off the card beside it, and four
// cards across clear their content by only single digits of pixels at the width
// a laptop gives this page -- see minItemWidth.
const GUTTER = 20;

// Must match the Tailwind gap-3 on the grid below; the operator offset is
// derived from it.
const GRID_GAP = 12;

export function FormulaCardRow({ items, minItemWidth = 230 }: FormulaCardRowProps) {
  // A grid rather than flex-wrap. Wrapped flex items keep growing to fill their
  // line, so four cards over two lines left the fourth one stretched across the
  // full width on its own -- one card as wide as the three above it. auto-fit
  // columns stay equal whatever the item count, and derive the column count
  // from the space available rather than from a breakpoint guessed per card
  // count (this row is four cards, or five with an appointment-type filter on).
  return (
    <div
      className="grid items-stretch gap-3"
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${minItemWidth}px, 1fr))` }}
    >
      {items.map((item) => (
        <div key={item.key} className="flex items-stretch">
          {/* Rendered even with no operator to keep the column widths equal. */}
          <div
            className="flex shrink-0 items-center justify-center text-[20px] font-medium text-gray-500"
            style={{
              width: `${GUTTER}px`,
              // The space between two cards is the grid gap PLUS this gutter,
              // but the gutter sits after the gap rather than straddling it,
              // so an operator centred in the gutter reads half a gap right
              // of centre. Shifted by half the gap as a transform, which
              // moves the glyph without taking those pixels off the card.
              transform: `translateX(-${GRID_GAP / 2}px)`,
            }}
            aria-hidden="true"
          >
            {item.op ?? ""}
          </div>
          {item.card}
        </div>
      ))}
    </div>
  );
}
